import { useMemo, useState, useEffect } from 'react';
import {
  Box, Stack, Typography, Tabs, Tab, TextField, IconButton, Drawer, useMediaQuery, useTheme,
} from '@mui/material';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { PmCard, PmInfoBox } from '../components/pm';
import { MarkdownRenderer, extractToc, type TocEntry } from '../components/help/MarkdownRenderer';

// MD-Files werden via Vite `?raw` als string importiert (lazy via dynamic import zur Bundle-Trennung)
const TAB_LOADERS: Record<TabKey, () => Promise<string>> = {
  user: () => import('../content/help/user-guide.md?raw').then((m) => m.default).catch(() => FALLBACK_MSG('Anwender-Handbuch')),
  tech: () => import('../content/help/tech-doc.md?raw').then((m) => m.default).catch(() => FALLBACK_MSG('Technische Dokumentation')),
  compliance: () => import('../content/help/compliance.md?raw').then((m) => m.default).catch(() => FALLBACK_MSG('Compliance-Handbuch')),
};

type TabKey = 'user' | 'tech' | 'compliance';

const FALLBACK_MSG = (title: string) =>
  `# ${title}\n\nDieser Bereich wird gerade befüllt. Inhalte folgen demnächst.`;

const TABS: Array<{ key: TabKey; label: string; icon: typeof MenuBookOutlinedIcon }> = [
  { key: 'user', label: 'Anwender-Handbuch', icon: MenuBookOutlinedIcon },
  { key: 'tech', label: 'Technische Doku', icon: CodeOutlinedIcon },
  { key: 'compliance', label: 'Compliance', icon: ShieldOutlinedIcon },
];

function TocSidebar({ toc, search, onSearchChange, onAnchorClick }: {
  toc: TocEntry[];
  search: string;
  onSearchChange: (v: string) => void;
  onAnchorClick: (anchor: string) => void;
}) {
  const q = search.trim().toLowerCase();
  const filtered = toc.filter((e) => !q || e.title.toLowerCase().includes(q));

  return (
    <Box sx={{ width: '100%', overflowY: 'auto', height: '100%' }}>
      <Box sx={{ p: 1.5, borderBottom: '1px solid var(--pm-bg-muted)' }}>
        <TextField
          size="small"
          fullWidth
          placeholder="In Inhalt suchen…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </Box>
      <Box sx={{ p: 1 }}>
        {filtered.length === 0 ? (
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)', display: 'block', px: 1, py: 1 }}>
            {q ? `Keine Treffer für „${search}"` : 'Inhaltsverzeichnis wird geladen…'}
          </Typography>
        ) : (
          filtered.map((entry, idx) => (
            <Box
              key={`${entry.anchor}-${idx}`}
              onClick={() => onAnchorClick(entry.anchor)}
              sx={{
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                pl: 1 + (entry.level - 1) * 1.5,
                borderRadius: 1,
                fontSize: entry.level === 1 ? '0.85rem' : entry.level === 2 ? '0.8rem' : '0.75rem',
                fontWeight: entry.level === 1 ? 700 : entry.level === 2 ? 600 : 400,
                color: entry.level === 1 ? 'var(--pm-fg-base)' : 'var(--pm-fg-muted)',
                '&:hover': { background: 'var(--pm-bg-deep)', color: 'var(--pm-fg-accent)' },
              }}
            >
              {entry.title}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

export function HelpCenterPage() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [tab, setTab] = useState<TabKey>('user');
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSearch('');
    TAB_LOADERS[tab]().then((md) => {
      setContent(md);
      setLoading(false);
    });
  }, [tab]);

  const toc = useMemo(() => extractToc(content), [content]);

  const handleAnchorClick = (anchor: string) => {
    const el = document.getElementById(anchor);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <HelpOutlineOutlinedIcon sx={{ fontSize: 28, color: 'var(--pm-fg-accent)' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Help Center</Typography>
          <Typography variant="caption" sx={{ color: 'var(--pm-fg-muted)' }}>
            Anleitungen, Architektur, Compliance-Mapping
          </Typography>
        </Box>
        {!isDesktop && (
          <IconButton size="small" onClick={() => setDrawerOpen(true)} aria-label="Inhaltsverzeichnis">
            <MenuOpenIcon />
          </IconButton>
        )}
      </Stack>

      <PmCard sx={{ mb: 2, flex: 0 }}>
        <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} variant="scrollable">
          {TABS.map(({ key, label, icon: Icon }) => (
            <Tab
              key={key}
              value={key}
              icon={<Icon fontSize="small" />}
              iconPosition="start"
              label={label}
              sx={{ minHeight: 48 }}
            />
          ))}
        </Tabs>
      </PmCard>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, gap: 2 }}>
        {isDesktop && (
          <Box sx={{
            width: 280,
            flexShrink: 0,
            background: 'var(--pm-bg-card, var(--pm-bg-base))',
            border: '1px solid var(--pm-bg-muted)',
            borderRadius: 'var(--pm-radius)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <TocSidebar toc={toc} search={search} onSearchChange={setSearch} onAnchorClick={handleAnchorClick} />
          </Box>
        )}
        <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
          <PmCard>
            {loading ? (
              <PmInfoBox variant="info">Lade Inhalt…</PmInfoBox>
            ) : (
              <MarkdownRenderer content={content} />
            )}
          </PmCard>
        </Box>
      </Box>

      <Drawer
        anchor="left"
        open={drawerOpen && !isDesktop}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: 300 } }}
      >
        <TocSidebar toc={toc} search={search} onSearchChange={setSearch} onAnchorClick={handleAnchorClick} />
      </Drawer>
    </Box>
  );
}
